import { DataConverter } from "../../Global/Base/Converters/DataConverter";
import { InvalidArgumentException } from "../../Global/Base/Exceptions";
import { CancellationToken } from "../../Global/Base/Promises/CancellationTokenSource";
import { HashType } from "../../Global/Base/Security/HashType";
import { IFileStreamReader } from "../../Global/Base/Streams/IFileStreamReader";
import { IReadableStream } from "./CryptoInterfaces";
import { TransferEncoding } from "./Messages/TransferEncoding";

/**
* Reader pro čtení stramů z nativní aplikaci.
* Podporuje pouze sekvenční čtení. (pro podporu náhodných přístupů je potřeba do nativní aplikace přidat seek na konkrétní pozici ve streamu).
*/
export class NativeAppStreamReader implements IFileStreamReader {
    private static readonly _blockSize: number = 500000;
    private readonly _stream: IReadableStream;
    private _isStreamRewinded: boolean = false; //true pokud se zavolala metoda begin(), která převine stream na začátek

    public get name(): string {
        return this._stream.name;
    }

    public get size(): number {
        return this._stream.size;
    }

    constructor(stream: IReadableStream) {
        this._stream = stream;
    }

    /**
     * Převine stream na začátek a nachystá reader pro čtení.
     */
    public async begin(): Promise<void> {
        await this._stream.begin();
        this._isStreamRewinded = true;
    }

    /**
     * Přečte požadovanou část streamu nebo celý stream
     * @param start Index prvního bytu, který se má začíst číst nebo nula
     * @param end Index posledního bytu, který se má přečíst nebo poslední byte
     * @param cancellationToken
     */
    public async read(start?: number, end?: number, cancellationToken: CancellationToken = CancellationToken.none): Promise<ArrayBuffer> {
        start = start === undefined ? 0 : start;
        end = end === undefined ? this._stream.size - 1 : end;

        if (end === undefined || end <= 0)
            throw new InvalidArgumentException("Cannot read stream. Invalid range specified.");
        if (start >= end)
            throw new InvalidArgumentException("Cannot read stream. Invalid range specified.");
        if (this._stream.position && this._stream.position !== start)
            throw new InvalidArgumentException("Stream does not support random accesses. Please read subsequent blocks or rewind stream.");

        cancellationToken.throwIfCancellationRequested();

        if (!this._isStreamRewinded)
            await this.begin();

        const result = new Uint8Array(end - start + 1);
        let arrayPosition = 0;
        while (this._stream.position <= end) {
            cancellationToken.throwIfCancellationRequested();

            //pokud je to možné načte celý blok, jinak přečte jen zbývající data
            const currentRange = this._stream.position + NativeAppStreamReader._blockSize <= end + 1 ? NativeAppStreamReader._blockSize : (this._stream.size <= end + 1 ? this._stream.size : end + 1) - this._stream.position;
            const resp = DataConverter.base64ToArray(await this._stream.readBlock(currentRange, TransferEncoding.Base64));
            result.set(resp, arrayPosition);
            arrayPosition += resp.length;
        }
        return result.buffer as ArrayBuffer;
    }

    public getHash(hashType: HashType): Promise<string | null> {
        return this._stream.getHash(hashType);
    }
}
